import { Button, Typography, makeStyles } from "@material-ui/core";
import { useState, useEffect } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import useToggle from "../../state/useToggle";
import { useToastError } from "../../components/FormError";
import api from "../../api";
import { toast } from "react-toastify";

const SKIP_REASON_LABELS = {
  exclusion_already_exists: "already on the exclusion list",
  invalid_ssn_format: "invalid SSN",
  missing_identifiers: "no email, phone, or SSN provided"
};

function skipReasonLabel(reason) {
  return SKIP_REASON_LABELS[reason] || reason;
}

export default function UniversalExclusionUpload({ onUploadSuccess }) {
  const classes = useStyles();
  const toggle = useToggle();
  const toastError = useToastError();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (toggle.on) {
      setFile(null);
      setIsSubmitting(false);
      setResult(null);
    }
  }, [toggle.on]);

  function handleFileChange(event) {
    const [selectedFile] = event.target.files || [];
    event.target.value = "";

    if (!selectedFile || selectedFile.type !== "text/csv") {
      toastError(new Error("Please select a valid CSV file"));
      return;
    }

    setFile(selectedFile);
  }

  function handleDownloadTemplate() {
    try {
      const csv = '"First name","Last name",Email,Phone,SSN\n';
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "universal_exclusion_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastError(new Error("Could not generate template file. Try a different browser."));
    }
  }

  async function handleSubmit() {
    if (!file) {
      toastError(new Error("Please select a CSV file to upload"));
      return;
    }

    const formData = new FormData();
    formData.append("csv", file);

    setIsSubmitting(true);
    try {
      const response = await api.uploadUniversalExclusionsCsv(formData);
      const { createdCount = 0, skipped = [] } = response?.data || {};

      if (skipped.length === 0) {
        toast.success(`${createdCount} added.`);
        toggle.turnOff();
      } else {
        setResult({ createdCount, skipped });
      }

      onUploadSuccess?.();
    } catch (error) {
      toastError(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        onClick={toggle.toggle}
      >
        Upload CSV
      </Button>

      <ConfirmationDialog
        open={toggle.on}
        onCancel={toggle.turnOff}
        onConfirm={handleSubmit}
        title="Upload universal exclusions CSV"
        cancelProps={{ children: result ? "Done" : "Close" }}
        hideConfirm={Boolean(result)}
        confirmProps={{
          children: isSubmitting ? "Submitting..." : "Submit",
          disabled: isSubmitting || !file
        }}
      >
        {result ? (
          <UploadResultSummary result={result} classes={classes} />
        ) : (
          <div className={classes.modalContent}>
            <Typography variant="body2" color="textSecondary">
              CSV must include <code>First name</code>, <code>Last name</code>,
              and at least one of: <code>Email</code>, <code>Phone</code>,{" "}
              <code>SSN</code>. Maximum 500 rows. For larger files use the Bulk
              Uploader.{" "}
              <Button
                size="small"
                color="primary"
                onClick={handleDownloadTemplate}
                style={{ textTransform: "none", padding: 0, minWidth: 0, verticalAlign: "baseline" }}
                disableRipple
                disableFocusRipple
              >
                Download template
              </Button>
              .
            </Typography>
            <div className={classes.fileRow}>
              <input
                accept=".csv"
                className={classes.fileInput}
                id="universal-exclusion-csv-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="universal-exclusion-csv-upload">
                <Button variant="outlined" component="span">
                  {file ? file.name : "Choose CSV"}
                </Button>
              </label>
            </div>
          </div>
        )}
      </ConfirmationDialog>
    </>
  );
}

function UploadResultSummary({ result, classes }) {
  return (
    <div className={classes.modalContent}>
      <Typography variant="body2">
        <strong>{result.createdCount}</strong>{" "}
        {result.createdCount === 1 ? "exclusion" : "exclusions"} added.{" "}
        <strong>{result.skipped.length}</strong> skipped.
      </Typography>
      <ul className={classes.skippedList}>
        {result.skipped.map((s, idx) => (
          <li key={`${s.row}-${idx}`}>
            Row {s.row} — {s.label || "(no name)"} — {skipReasonLabel(s.reason)}
          </li>
        ))}
      </ul>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0)
  },
  fileRow: {
    marginTop: theme.spacing(1)
  },
  fileInput: {
    display: "none"
  },
  skippedList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary
  }
}));
